import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Menu, Sun, Moon } from "lucide-react";
import useTheme from "./useTheme";

const links = [
  { href: "#memory", label: "Memory" },
  { href: "#voice", label: "Voice" },
  { href: "#graph", label: "Graph" },
  { href: "#pricing", label: "Pricing" },
];

const Nav = () => {
  const { scrollY } = useScroll();
  const blur = useTransform(scrollY, [0, 120], [6, 18]);
  const bg = useTransform(scrollY, [0, 120], ["rgb(var(--bg-rgb) / 0.30)", "rgb(var(--bg-rgb) / 0.75)"]);
  const [open, setOpen] = useState(false);
  const { effective, toggle } = useTheme();

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <motion.nav
      data-testid="nav-bar"
      style={{ backdropFilter: `blur(${blur.get ? blur.get() : 12}px)`, background: bg }}
      className="fixed top-0 inset-x-0 z-40 border-b border-panel/5"
    >
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        <a href="#top" className="flex items-center gap-3" data-testid="nav-logo">
          <span className="relative inline-block w-2.5 h-2.5 rounded-full bg-aura-cyan shadow-[0_0_12px_rgb(var(--accent-cyan-rgb)/0.9)]">
            <span className="absolute inset-0 rounded-full animate-ping bg-aura-cyan/60" />
          </span>
          <span className="font-display text-[22px] tracking-[-0.02em] text-ink font-light">
            AURA<span className="text-aura-cyan">.</span>
          </span>
        </a>

        <div className="hidden md:flex items-center gap-8 font-mono text-[11px] tracking-[0.22em] uppercase text-whisper">
          {links.map((l) => (
            <a key={l.href} href={l.href} className="hover:text-ink transition-colors" data-testid={`nav-link-${l.label.toLowerCase()}`}>
              {l.label}
            </a>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={toggle}
            aria-label={`Switch to ${effective === "dark" ? "light" : "dark"} mode`}
            className="theme-swap hidden sm:inline-flex"
            data-testid="nav-theme-toggle"
            title={`${effective === "dark" ? "Light" : "Dark"} mode`}
          >
            <span className="knob grid place-items-center">
              {effective === "dark" ? <Moon size={10} className="text-ink" /> : <Sun size={10} className="text-ink" />}
            </span>
          </button>
          <Link to="/auth" className="btn-ghost hidden sm:inline-flex" data-testid="nav-open-dashboard">Sign in</Link>
          <Link to="/auth" className="btn-primary" data-testid="nav-cta-primary">Get started</Link>
          <button
            className="md:hidden w-10 h-10 grid place-items-center border border-panel/10 rounded-full"
            onClick={() => setOpen(!open)}
            aria-label="Open menu"
            data-testid="nav-menu-toggle"
          >
            <Menu size={16} />
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden border-t border-panel/5 bg-base/90 backdrop-blur-xl">
          <div className="max-w-7xl mx-auto px-6 py-4 flex flex-col gap-3 font-mono text-xs tracking-[0.22em] uppercase text-whisper">
            <Link to="/auth" className="py-2 border-b border-panel/5 text-aura-cyan" data-testid="nav-mobile-open-dashboard">Sign in</Link>
            {links.map((l) => (
              <a key={l.href} href={l.href} className="py-2 border-b border-panel/5" onClick={() => setOpen(false)} data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}>
                {l.label}
              </a>
            ))}
            <button onClick={toggle} className="py-2 text-left" data-testid="nav-mobile-theme-toggle">
              Theme · {effective}
            </button>
          </div>
        </div>
      )}
    </motion.nav>
  );
};

export default Nav;
